import { PrintScoreSheets } from './PrintScoreSheets';
import { ShowScoreQr } from './ShowScoreQr';
import { PrintNameCards, PrintTableNumbers } from './PrintTableNumbers';
import { paceLabel, type PlayingPace } from '@league/shared/schemas';
import { seatingProblems } from '@league/shared/seating';
import type { AbsenceListResponse, SeatingSaveResponse } from '@league/shared/contract';
import { SEATING_STATUS } from '@league/shared/status';
import { LINK_CLASS, StatusChip } from '@league/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LeaguePage } from '../leagues/LeagueHeader';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Button, ErrorNote } from '../../components/ui';
import { ApiError, apiFetch, apiPost } from '../../lib/api';
import { useHowTo } from '../../components/HowTo';
import { seatingHowTo } from '../../content/how-to';
import { Absences } from './Absences';
import { ConstraintPanel } from './ConstraintPanel';
import { NotSeated } from './NotSeated';
import { chairKey, parseChairKey, useSeatDrag, type SeatDrag } from './useSeatDrag';

type Seat = 'east' | 'south' | 'west' | 'north' | 'floater';

interface SeatedPlayer {
  isSubstitute?: boolean;
  enrollmentId: string;
  playerId: string;
  firstName: string;
  lastName: string;
  pace?: PlayingPace;
  skillLevel: 'beginner' | 'intermediate' | 'advanced';
  score: number;
  seat: Seat;
}

interface SeatedTable {
  tableNumber: number;
  seats: SeatedPlayer[];
}

interface Note {
  kind: string;
  message: string;
  playerIds: string[];
}

/**
 * Whether the board may be touched, asked of the SESSION. `SeatingPlan.canRegenerate` says the same
 * thing but only exists once something has been saved — and the case that matters is a scored
 * session with no arrangement at all.
 */
interface SeatingLock {
  published: boolean;
  /** Terminal. The arrangement is now a record of who sat where when the scores were taken. */
  scored: boolean;
  closed: boolean;
  scoredPlayerIds: string[];
  protectedTableNumbers: number[];
}

interface SeatingPlan {
  sessionId: string;
  sessionNumber: number;
  tables: SeatedTable[];
  notes: Note[];
  publishedAt: string | null;
  canRegenerate: boolean;
  version: string;
}

/** Counter-clockwise, as at a real table. East deals. */
const SEAT_ORDER: Seat[] = ['east', 'south', 'west', 'north', 'floater'];

/**
 * The seating board.
 *
 * A square table with chairs around it, because that is what a director is looking at in the room.
 * CHAIRS are the targets, not the table: tap a player, tap a chair, and if that chair is taken the
 * two swap — swapping is the move a director actually wants, and rejecting the drop would just
 * make them do it in two steps.
 *
 * TWO WAYS TO MOVE SOMEBODY, and they are the same move underneath.
 *
 *   DRAG   Pick a player up and drop her on a chair. What the owner asked for, and what anyone
 *          reaches for first on a laptop.
 *   TAP    Tap a player, tap a chair. The phone path, and the keyboard one — every chair is a real
 *          button, so tab and Enter do exactly what tapping does.
 *
 * Both end in `placePlayer`, so there is one definition of what a move means and the two cannot
 * drift. Touch deliberately does not drag: claiming the gesture would mean a board of seven tables
 * that cannot be scrolled past. See `useSeatDrag`.
 */
export function SeatingBoard() {
  const { sessionId } = useParams<{ sessionId: string }>();
  return <SeatingWorkspace key={sessionId} />;
}

function SeatingWorkspace() {
  const howTo = useHowTo(seatingHowTo());
  const { sessionId } = useParams<{ sessionId: string }>();
  const queryClient = useQueryClient();

  /** The arrangement being looked at. Nothing is written until Save. */
  const [arrangement, setDraft] = useState<SeatingPlan | null>(null);
  const [editingFinalized, setEditingFinalized] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [undo, setUndo] = useState<SeatingPlan[]>([]);
  const recoveryKey = `seating-draft:${sessionId}`;
  const restored = useRef<string | null>(null);
  const recoveryPending = useRef(false);
  const knownPlayers = useRef(new Map<string, SeatedPlayer>());
  function edit(next: SeatingPlan, clearNotes = true) {
    if (readOnly) return;
    setFinalizedMessage(false);
    if (draft) setUndo(history => [...history, draft]);
    setDraft({ ...next, version: next.version || draft?.version || saved.data?.seating?.version || '', notes: clearNotes ? [] : next.notes });
    setDirty(true);
    setPicked(null);
  }
  function setAside(id: string) {
    if (!draft || readOnly) return;
    edit({ ...draft, tables: draft.tables.map(t => ({ ...t, seats: t.seats.filter(p => p.enrollmentId !== id) })) });
  }
  const [picked, setPicked] = useState<string | null>(null);
  const [seed, setSeed] = useState(152);

  /**
   * Dropping is the same move as tapping, so it goes through the same function. A drop on anything
   * that is not a chair is a move abandoned, which is what letting go over nothing means.
   */
  const drag = useSeatDrag((key, enrollmentId) => {
    if (key === 'waiting') { setAside(enrollmentId); return; }
    const target = parseChairKey(key);
    if (!target) return;
    placePlayer(enrollmentId, target.tableNumber, target.seat as Seat);
  });

  const saved = useQuery({
    queryKey: ['seating', sessionId],
    queryFn: () =>
      apiFetch<{
        seating: SeatingPlan | null;
        lock: SeatingLock | null;
        marketId: string | null;
        leagueId?: string;
        sessionNumber: number;
        emailPreviewHref?: string | null;
        nameCardsReady?: boolean;
        attendingRoster?: { enrollmentId: string; name: string; firstName?: string; lastName?: string; status: string }[];
        roster: {
          playerId?: string;
          isSubstitute?: boolean;
          enrollmentId: string;
          name: string;
          firstName?: string;
          lastName?: string;
          status: 'enrolled' | 'registered' | 'waitlist';
          pace?: PlayingPace;
          skillLevel: string;
        }[];
      }>(`/api/sessions/${sessionId}/seating`),
    enabled: Boolean(sessionId),
    refetchOnMount: 'always',
  });

  // Recovery and Undo retain positions, not an old copy of a player's pace.
  const draft = useMemo(() => arrangement ? {
    ...arrangement,
    tables: arrangement.tables.map(table => ({ ...table, seats: table.seats.map(player => {
      const current = saved.data?.roster.find(p => p.enrollmentId === player.enrollmentId && p.playerId === player.playerId);
      return current ? { ...player, pace: current.pace ?? 'unknown' } : player;
    }) })),
  } : null, [arrangement, saved.data?.roster]);

  useEffect(() => {
    if (draft) for (const t of draft.tables) for (const p of t.seats) knownPlayers.current.set(p.enrollmentId, p);
  }, [draft]);
  useEffect(() => {
    if (!dirty || !draft) return;
    try { sessionStorage.setItem(recoveryKey, JSON.stringify({ ...draft, editingFinalized })); } catch { /* Navigation confirmation still protects edits. */ }
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    const link = (event: MouseEvent) => {
      if (!(event.target instanceof Element) || !event.target.closest('a[href]:not([download])')) return;
      if (!window.confirm('Leave with unsaved seating changes? A recovery draft will remain in this tab.')) { event.preventDefault(); event.stopPropagation(); }
    };
    window.addEventListener('beforeunload', warn);
    document.addEventListener('click', link, true);
    return () => { window.removeEventListener('beforeunload', warn); document.removeEventListener('click', link, true); };
  }, [dirty, draft, recoveryKey, editingFinalized]);
  useEffect(() => {
    // A cached-query remount can replay this effect before restored dirty state commits.
    // Never let that replay overwrite the recovered arrangement with the saved baseline.
    if (dirty) recoveryPending.current = false;
    if (saved.data && !dirty && !recoveryPending.current) {
      if (restored.current !== sessionId) {
        restored.current = sessionId ?? null;
        if (!saved.data.lock?.closed) {
          try {
            const recovery = sessionStorage.getItem(recoveryKey);
            if (recovery) {
              const recovered = JSON.parse(recovery);
              if (recovered.sessionId === sessionId && recoveryFits(recovered) && (recovered.editingFinalized ? saved.data.lock?.published : !saved.data.lock?.published && !saved.data.lock?.scored)) {
                recoveryPending.current = true;
                setDraft({ ...recovered, notes: Array.isArray(recovered.notes) ? recovered.notes : [] }); setEditingFinalized(Boolean(recovered.editingFinalized)); setDirty(true); return;
              }
            }
          } catch { /* An unreadable recovery never replaces saved seating. */ }
        } else sessionStorage.removeItem(recoveryKey);
      }
      setDraft(saved.data.seating);
    }
  }, [saved.data, dirty, sessionId, recoveryKey]);

  /*
    ESCAPE PUTS HER DOWN AGAIN.

    A mouse user who picks somebody up and changes her mind simply does not drop — the gesture has
    a natural way out. Tapping and keyboard do not: once a player is in hand, every chair is a
    destination and there is no way to hold nothing again. She was stuck carrying somebody until
    she put them somewhere, which is the sort of thing that ends with a player in the wrong seat.

    Found by driving the board with the keyboard rather than by reading it.
  */
  useEffect(() => {
    if (!picked) return;

    const cancel = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPicked(null);
    };

    window.addEventListener('keydown', cancel);
    return () => window.removeEventListener('keydown', cancel);
  }, [picked]);

  /** Declared here rather than beside the buttons because `placePlayer` refuses on them. */
  const published = Boolean(draft?.publishedAt || saved.data?.lock?.published);
  /**
   * Scores exist, so nothing on this board may move — not the chairs, and not the offer to arrange
   * them in the first place. Read from the session, so it is known even when there is no
   * arrangement to read it from.
   */
  const scored = Boolean(saved.data?.lock?.scored);
  const closed = Boolean(saved.data?.lock?.closed);
  const readOnly = closed || ((published || scored) && !editingFinalized);

  /** Who is away, so the board can leave them out of "unseated" and list them as absent. */
  const absences = useQuery({
    queryKey: ['absences', sessionId],
    queryFn: () =>
      apiFetch<AbsenceListResponse>(`/api/sessions/${sessionId}/absences`),
    enabled: Boolean(sessionId),
  });

  const [finalizedMessage, setFinalizedMessage] = useState(false);

  const unseatedRoster = saved.data?.roster ?? [];
  const awayIds = new Set(
    (absences.data?.absences ?? []).map((a) => a.absence.absentEnrollmentId),
  );

  /*
    AN EMPTY BOARD, WHEN NOTHING IS SAVED (Littleton, 2026-10-30). The search's own failure message
    says to arrange the tables manually, and without this the board only offered the search again.
    One table for every three players, because a roster that needs this is usually one that needs
    threes; publishing removes whichever stay empty.
  */
  function arrangeByHand() {
    generate.reset();
    const players = unseatedRoster.filter(p => p.status === 'enrolled' && !awayIds.has(p.enrollmentId)).length;
    const tables = Array.from({ length: Math.max(1, Math.ceil(players / 3)) }, (_, i) => ({ tableNumber: i + 1, seats: [] }));
    edit({ sessionId: sessionId ?? '', sessionNumber: saved.data?.sessionNumber ?? 0, tables, notes: [], publishedAt: null, canRegenerate: true, version: '' });
  }

  const generate = useMutation({
    mutationFn: (nextSeed: number) =>
      apiPost<{ seating: SeatingPlan }>(`/api/sessions/${sessionId}/seating/generate`, {
        seed: nextSeed,
      }),
    onSuccess: ({ seating }) => {
      edit(seating, false);
    },
  });

  const save = useMutation({
    mutationFn: () =>
      apiFetch<SeatingSaveResponse>(`/api/sessions/${sessionId}/seating${editingFinalized ? "/finalized" : ""}`, {
        method: 'PUT',
        body: JSON.stringify({
          ...(editingFinalized ? { expectedVersion: draft?.version } : {}),
          tables: (draft?.tables ?? []).map((table) => ({
            tableNumber: table.tableNumber,
            seats: table.seats.map((s) => ({ seat: s.seat, enrollmentId: s.enrollmentId })),
          })),
          notes: (draft?.notes ?? []).map((n) => n.message),
        }),
      }),
    onSuccess: async ({ seating }) => {
      setDraft(seating);
      setEditingFinalized(false);
      setDirty(false);
      setUndo([]);
      sessionStorage.removeItem(recoveryKey);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['seating', sessionId] }),
        queryClient.invalidateQueries({ queryKey: ['director-leagues'] }),
        queryClient.invalidateQueries({ queryKey: ['league'] }),
        queryClient.invalidateQueries({ queryKey: ['director-dashboard'] }),
        queryClient.invalidateQueries({ queryKey: ['director-next-steps'] }),
      ]);
    },
  });

  const publish = useMutation({
    mutationFn: () =>
      apiPost<SeatingSaveResponse>(`/api/sessions/${sessionId}/seating/finalize`, {
        expectedVersion: draft?.version || null,
        tables: (draft?.tables ?? []).map(table => ({ tableNumber: table.tableNumber, seats: table.seats.map(s => ({ seat: s.seat, enrollmentId: s.enrollmentId })) })),
        notes: (draft?.notes ?? []).map(note => note.message),
      }),
    onSuccess: async ({ seating }) => {
      setFinalizedMessage(true);
      setDraft(seating);
      setEditingFinalized(false);
      setDirty(false);
      setUndo([]);
      sessionStorage.removeItem(recoveryKey);
      await queryClient.invalidateQueries({ queryKey: ['seating', sessionId] });
      await queryClient.invalidateQueries({ queryKey: ['league'] });
      await queryClient.invalidateQueries({ queryKey: ['director-dashboard'] });
      await queryClient.invalidateQueries({ queryKey: ['director-next-steps'] });
    },
  });

  /*
    REOPEN SEATING (owner, 2026-10-25). Finalized seating could only be edited table by table, and
    the server's reopen had no button. Refused once scores exist; it emails nobody — an unsent
    "your table is up" is superseded, and finalizing again tells players afresh.
  */
  const [confirmReopen, setConfirmReopen] = useState(false);
  const reopen = useMutation({
    mutationFn: () => apiPost<SeatingSaveResponse>(`/api/sessions/${sessionId}/seating/unpublish`, {}),
    onSuccess: async ({ seating }) => {
      setConfirmReopen(false);
      setFinalizedMessage(false);
      setDraft(seating);
      setEditingFinalized(false);
      setDirty(false);
      setUndo([]);
      sessionStorage.removeItem(recoveryKey);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['seating', sessionId] }),
        queryClient.invalidateQueries({ queryKey: ['league'] }),
        queryClient.invalidateQueries({ queryKey: ['director-dashboard'] }),
        queryClient.invalidateQueries({ queryKey: ['director-next-steps'] }),
      ]);
    },
  });

  /**
   * Moving someone into a chair: empty chair moves them, occupied chair swaps the two. Swapping is
   * the move a director actually wants, and refusing the drop would only make her do it in two
   * steps.
   *
   * Takes the mover explicitly rather than reading `picked`, because the drag knows who it is
   * carrying and the tap path has to look it up. One function, two ways in.
   */
  function placePlayer(moverId: string, tableNumber: number, seatName: Seat) {
    if (!draft || readOnly) return;

    const next: SeatedTable[] = draft.tables.map((t) => ({ ...t, seats: [...t.seats] }));

    let fromTable = -1;
    let fromIndex = -1;
    next.forEach((table, ti) => {
      const si = table.seats.findIndex((s) => s.enrollmentId === moverId);
      if (si !== -1) {
        fromTable = ti;
        fromIndex = si;
      }
    });
    const toTable = next.findIndex((t) => t.tableNumber === tableNumber);
    if (toTable === -1) return;

    /**
     * The picked player may not be seated at all — she came from the "not at a table" list. There
     * is nobody to swap with, so whoever is in the chair is displaced INTO that list rather than
     * silently disappearing, and the panel says so in red until she is placed again.
     */
    if (fromTable === -1) {
      const arriving = unseatedRoster.find((person) => person.enrollmentId === moverId);
      if (!arriving || arriving.status !== 'enrolled' || awayIds.has(moverId)) return;

      const occupantIndex = next[toTable]!.seats.findIndex((s) => s.seat === seatName);
      const seatedPlayer = {
        enrollmentId: arriving.enrollmentId,
        playerId: arriving.playerId ?? arriving.enrollmentId,
        isSubstitute: Boolean(arriving.isSubstitute),
        firstName: arriving.firstName ?? arriving.name.split(' ')[0] ?? arriving.name,
        lastName: arriving.lastName ?? arriving.name.split(' ').slice(1).join(' '),
        skillLevel: arriving.skillLevel as SeatedPlayer['skillLevel'],
        pace: arriving.pace ?? 'unknown',
        score: 0,
        ...knownPlayers.current.get(moverId),
        seat: seatName,
      };

      if (occupantIndex === -1) next[toTable]!.seats.push(seatedPlayer);
      else next[toTable]!.seats[occupantIndex] = seatedPlayer;

      for (const table of next) {
        table.seats.sort((a, b) => SEAT_ORDER.indexOf(a.seat) - SEAT_ORDER.indexOf(b.seat));
      }

      edit({ ...draft, tables: next });
      return;
    }

    const mover = next[fromTable]!.seats[fromIndex]!;
    const occupantIndex = next[toTable]!.seats.findIndex((s) => s.seat === seatName);

    if (occupantIndex === -1) {
      next[fromTable]!.seats.splice(fromIndex, 1);
      next[toTable]!.seats.push({ ...mover, seat: seatName });
    } else {
      const occupant = next[toTable]!.seats[occupantIndex]!;
      next[toTable]!.seats[occupantIndex] = { ...mover, seat: seatName };
      next[fromTable]!.seats[fromIndex] = { ...occupant, seat: mover.seat };
    }

    for (const table of next) {
      table.seats.sort((a, b) => SEAT_ORDER.indexOf(a.seat) - SEAT_ORDER.indexOf(b.seat));
    }

    edit({ ...draft, tables: next });
  }

  /**
   * Applies the panel's suggestion: exchange two players, keeping each one's chair.
   *
   * Written separately from the tap-to-move path above rather than expressed in terms of it. That
   * one is "move this player to that chair" and swaps only as a side effect of the chair being
   * taken; this one is "these two exchange places", which is a different intent and would be
   * fragile expressed as two moves.
   */
  function canApplySuggestion(a: string, b: string) {
    if (!draft || readOnly || busy || problems.length || affectedScored.length) return false;
    const tables = draft.tables.filter(t => t.seats.some(p => p.enrollmentId === a || p.enrollmentId === b));
    if (a === b || tables.length !== 2) return false;
    if (!editingFinalized) return true;
    return tables.every(t => !(saved.data?.lock?.protectedTableNumbers ?? []).includes(t.tableNumber)
      && [...t.seats, ...(saved.data?.seating?.tables.find(original => original.tableNumber === t.tableNumber)?.seats ?? [])]
        .every(p => !(saved.data?.lock?.scoredPlayerIds ?? []).includes(p.playerId)));
  }

  function applySwap(a: string, b: string) {
    if (!canApplySuggestion(a, b)) return;
    if (!draft) return;

    const seatOf = (id: string) =>
      draft.tables.flatMap((t) => t.seats).find((seat) => seat.enrollmentId === id);

    const first = seatOf(a);
    const second = seatOf(b);
    if (!first || !second) return;

    const next = draft.tables.map((table) => ({
      ...table,
      seats: table.seats
        .map((seat) => {
          if (seat.enrollmentId === a) return { ...second, seat: seat.seat };
          if (seat.enrollmentId === b) return { ...first, seat: seat.seat };
          return seat;
        })
        .sort((x, y) => SEAT_ORDER.indexOf(x.seat) - SEAT_ORDER.indexOf(y.seat)),
    }));

    edit({ ...draft, tables: next });
  }

  const eligible = unseatedRoster.filter(p => p.status === 'enrolled' && !awayIds.has(p.enrollmentId));
  const problems = draft ? seatingProblems(draft.tables, eligible) : [];
  if (absences.isPending || absences.error) problems.push('Waiting for current participant eligibility.');
  const affectedScored = editingFinalized && draft ? draft.tables.filter(table => {
    const original = saved.data?.seating?.tables.find(t=>t.tableNumber === table.tableNumber);
    const signature = (t?: SeatedTable) => t?.seats.map(p=>`${p.seat}:${p.enrollmentId}`).sort().join('|');
    return signature(table) !== signature(original) && ((saved.data?.lock?.protectedTableNumbers ?? []).includes(table.tableNumber) || [...table.seats, ...(original?.seats ?? [])].some(p=>(saved.data?.lock?.scoredPlayerIds ?? []).includes(p.playerId)));
  }) : [];
  const visibleProblems = problems.map(p => p.replace('publishing', 'finalizing'));
  const busy = save.isPending || generate.isPending || publish.isPending || reopen.isPending;

  if (saved.isPending) {
    return <p className="px-6 py-10 text-sm text-ink-3">Loading…</p>;
  }

  /**
   * Who the carried player would displace, or null over an empty chair or her own. Read from the
   * draft rather than the DOM so it is the same source of truth the drop itself uses.
   */
  const swapPartner = (() => {
    const over = drag.dragging?.over;
    if (!draft || !over) return null;

    const target = parseChairKey(over);
    if (!target) return null;

    const occupant = draft.tables
      .find((table) => table.tableNumber === target.tableNumber)
      ?.seats.find((seat) => seat.seat === target.seat);

    if (!occupant || occupant.enrollmentId === drag.dragging?.enrollmentId) return null;
    return `${occupant.firstName} ${occupant.lastName}`;
  })();

  /**
   * Who is currently in hand, by name.
   *
   * A mouse user has the drag ghost following the pointer and a picked chair goes solid — both are
   * answers to "who am I carrying". Neither exists for somebody using a screen reader, who can tab
   * away from the chair she lifted from and then has nothing at all. This is the same fact, said in
   * words.
   */
  const heldPlayer = (() => {
    if (!picked || !draft) return null;

    for (const table of draft.tables) {
      const seat = table.seats.find((s) => s.enrollmentId === picked);
      if (seat) return `${seat.firstName} ${seat.lastName}`;
    }

    /*
      AND THE NOT-AT-A-TABLE LIST, which this used to leave out.
        
      It searched `draft.tables` only, so picking up a woman who was already sitting somewhere was
      announced and picking up one who had no chair yet was silent — and the second is the move a
      director makes most. The board still relabelled every chair to "Put them in North, which is
      empty", so a screen-reader user knew a placement was pending but not WHO was pending.

      Found by the browser suite on its first run, which is exactly the population it was built for.
    */
    const standing = unseatedRoster.find((person) => person.enrollmentId === picked);
    return standing ? standing.name : null;
  })();

  const error =
    (generate.error ?? save.error ?? publish.error ?? reopen.error) instanceof ApiError
      ? ((generate.error ?? save.error ?? publish.error ?? reopen.error) as ApiError).message
      : (generate.error ?? save.error ?? publish.error ?? reopen.error) ? 'Could not save that change. Your arrangement is preserved; try again.' : null;

  /*
    Inside the league's own page frame — its header, "← All leagues" and tabs where they sit on every
    league page (owner, 2026-10-15). Each way out asks first when the arrangement is unsaved.
  */
  const leaveWithUnsaved = () => !dirty || window.confirm('Leave with unsaved seating changes? A recovery draft will remain in this tab.');
  const frame = (content: React.ReactNode) =>
    saved.data?.leagueId ? (
      <LeaguePage leagueId={saved.data.leagueId} current="Seating" onLeave={leaveWithUnsaved}>
        {content}
      </LeaguePage>
    ) : (
      <div className="mx-auto max-w-4xl px-6 py-10">{content}</div>
    );

  return frame(
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <div className="flex items-center gap-3">
          <h1 className="font-display text-3xl leading-tight">Seating</h1>
          {howTo.button}
          {/*
            THE KIT'S CHIP, not a hand-built pill. This was `rounded-full` — a radius the system does
            not contain — filled with the accent at 25% alpha rather than the signal tint, at 12px
            rather than 11, and the wrong tone besides: publishing is finished, which is sage.
          */}
          {published ? <StatusChip status={{ ...SEATING_STATUS.published, label: 'Finalized' }} /> : null}
        </div>
        {howTo.panel}
        {/* "Yet" is a promise. On a session whose scores are in there is no yet. */}
        <p className="text-[14px] text-ink-2">
          {draft
            ? `Session ${draft.sessionNumber} · ${draft.tables.length} table${draft.tables.length === 1 ? '' : 's'}`
            : scored
              ? 'Closed — the scores for this session are in.'
              : 'Nothing arranged yet.'}
        </p>
      </header>

      {/*
        PRINTING FOR THE NIGHT, AT THE TOP — owner, 2026-10-15: it sat under the tables, and finding
        where to print the score sheets was "really hard". Here, it is the first thing under
        "Finalized": table numbers, and the league's score sheet.
      */}
      {draft ? (
        <div className="flex flex-wrap gap-2">
          <PrintTableNumbers sessionId={sessionId ?? ''} hasSavedTables={Boolean(saved.data?.seating?.tables.some(t => t.seats.length > 0))} tableNumbersChanged={JSON.stringify([...new Set(saved.data?.seating?.tables.filter(t => t.seats.length > 0).map(t => t.tableNumber) ?? [])].sort((a, b) => a - b)) !== JSON.stringify([...new Set(draft.tables.filter(t => t.seats.length > 0).map(t => t.tableNumber))].sort((a, b) => a - b))} />
          {saved.data?.nameCardsReady && !dirty ? <PrintNameCards sessionId={sessionId ?? ''} /> : null}
          {saved.data?.leagueId ? (
            <>
              <PrintScoreSheets leagueId={saved.data.leagueId} variant="button" />
              <ShowScoreQr leagueId={saved.data.leagueId} />
            </>
          ) : null}
        </div>
      ) : null}

      {!draft && error ? <ErrorNote>{error}</ErrorNote> : null}

      {/*
        Above the board, because it is an INPUT to it. Marking somebody away after generating an
        arrangement means the arrangement is now wrong, and the order on screen should say so.
      */}
      {sessionId ? (
        <Absences
          sessionId={sessionId}
          marketId={saved.data?.marketId ?? null}
          playing={saved.data?.attendingRoster ?? saved.data?.roster ?? []}
          onChanged={() => {
            void saved.refetch().then(({data}) => {
              if (!data) return;
              const eligibleIds = new Set(data.roster.map(p => p.enrollmentId));
              setDraft(current => current ? { ...current, tables: current.tables.map(t => ({ ...t, seats: t.seats.filter(p => eligibleIds.has(p.enrollmentId)) })) } : current);
              setUndo([]);
              setPicked(null);
            });
          }}
        />
      ) : null}

      {draft && !readOnly ? <section className="flex flex-col gap-3">
        <h2 className="font-display text-xl">Arrange your tables</h2>
        <details className="text-sm text-ink-2"><summary className="cursor-pointer focus-visible:outline focus-visible:outline-2">How to move players</summary><p>Drag a player, or select their name and then an empty seat. Select an occupied seat to swap. Set players aside in Players to seat while rearranging. Press Escape to cancel selection.</p></details>
        <div className="flex flex-wrap items-center gap-3">
          {editingFinalized ? null : <Button variant="secondary" disabled={busy} onClick={() => {
            if (!window.confirm('Replace this arrangement, including any saved draft edits, with a generated proposal? You can undo a successful replacement.')) return;
            const next = seed + 1;
            setSeed(next);
            generate.mutate(next);
          }}>Try another arrangement</Button>}
          <Button variant="secondary" disabled={busy || !draft.tables.some(t => t.seats.length)} onClick={() => edit({ ...draft, tables: draft.tables.map(t => ({ ...t, seats: [] })) })}>Clear all players</Button>
          <Button variant="secondary" disabled={busy || undo.length === 0} onClick={() => {
            const previous = undo[undo.length - 1];
            if (previous) { setDraft(previous); setUndo(undo.slice(0, -1)); setDirty(true); setPicked(null); }
          }}>Undo</Button>
          {dirty ? <span role="status" className="text-[13px] text-ink-2">Unsaved changes</span> : null}
        </div>
      </section> : null}

      {affectedScored.length ? <p role="alert" className="rounded-md border border-light bg-sand p-4 text-[13px]">
        These changes affect recorded scores: {affectedScored.map(t => `Table ${t.tableNumber}`).join(', ')}{(() => { const names = [...new Set(affectedScored.flatMap(t => [...t.seats, ...(saved.data?.seating?.tables.find(o => o.tableNumber === t.tableNumber)?.seats ?? [])]).filter(p => (saved.data?.lock?.scoredPlayerIds ?? []).includes(p.playerId)).map(p => `${p.firstName} ${p.lastName}`))]; return names.length ? ` (${names.join(', ')})` : ''; })()}. Keep those players and tables unchanged; you can still edit unrelated unscored tables. Use Undo to return to your previous arrangement.
      </p> : null}
      {draft ? (
        <NotSeated
          roster={unseatedRoster}
          seatedIds={new Set(draft.tables.flatMap((t) => t.seats.map((x) => x.enrollmentId)))}
          awayIds={awayIds}
          picked={picked}
          drag={readOnly ? null : drag}
          onPick={setPicked}
          onSetAside={setAside}
          disabled={readOnly || busy}
        />
      ) : null}


      {draft && !published && draft.notes.length > 0 ? (
        <ul className="flex flex-col gap-1.5 rounded-md border border-light bg-sand px-4 py-3">
          {draft.notes.map((note, index) => (
            <li key={index} className="text-[13px] text-ink-2">
              {note.message}
            </li>
          ))}
        </ul>
      ) : null}

      {/*
        Nothing saved. Two very different reasons, and offering the same button for both is what
        sent a director through arranging twenty-two people before the save refused her.
      */}
      {!draft ? (
        <div className="rounded-md border border-dashed border-light px-6 py-10 text-center">
          {scored && !editingFinalized && !published ? (
            <>
              <p className="text-[15px]">This session was never arranged here.</p>
              <p className="mx-auto mt-1 max-w-[34rem] text-[13px] text-ink-3">
                Its scores are already in, so the seating is closed — an arrangement now would
                describe a session that has been played. Nothing to do on this screen.
              </p>
            </>
          ) : (
            <>
              <p className="text-[15px]">No seating for this session yet.</p>
              <p className="mt-1 text-[13px] text-ink-3">
                The arrangement honours who asked to sit together, then plays like with like.
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-3">
                <Button disabled={generate.isPending} onClick={() => generate.mutate(seed)}>
                  {generate.isPending ? 'Working…' : 'Arrange the tables'}
                </Button>
                <Button variant="secondary" disabled={generate.isPending} onClick={arrangeByHand}>
                  Arrange by hand
                </Button>
              </div>
            </>
          )}
        </div>
      ) : null}

      {draft ? (
        <>
          {scored && !editingFinalized && !published ? (
            <p className="rounded-md border border-light bg-sand px-4 py-2.5 text-[13px] text-ink-2">
              Scores are in for this session, so where people sat is now part of the record and
              cannot be changed.
            </p>
          ) : null}


          {/*
            SAID OUT LOUD, and directly above the board.

            `aria-live="polite"` announces who is in hand the moment she is picked up, which is the
            keyboard equivalent of the ghost that follows a pointer. It renders visibly too, because
            somebody who tabbed away from the chair she lifted from has nothing else to remind her,
            and a sighted keyboard user is not a screen-reader user.

            Sticky, so it stays with her while she scrolls a board of seven tables looking for the
            chair she wants.
          */}
          {heldPlayer ? (
            <p
              aria-live="polite"
              className="sticky top-2 z-10 rounded-md bg-deep px-4 py-2.5 text-[13px] text-on-deep shadow-sm"
            >
              Holding {heldPlayer}. Choose a chair, or press Escape to put them back.
            </p>
          ) : (
            /* Present but empty, so the region exists before it has anything to announce. */
            <p aria-live="polite" className="sr-only" />
          )}

          <div className="grid gap-8 sm:grid-cols-2">
            {draft.tables.map((table) => (
              <div key={table.tableNumber} id={`table-${table.tableNumber}`} className="min-w-0">
              <Table
                key={table.tableNumber}
                table={table}
                picked={picked}
                disabled={readOnly || busy}
                onClear={() => edit({ ...draft, tables: draft.tables.map(t => t.tableNumber === table.tableNumber ? { ...t, seats: [] } : t) })}
                drag={drag}
                onPick={setPicked}
                onPlace={(tableNumber, seat) => {
                  if (picked) placePlayer(picked, tableNumber, seat);
                }}
              />
              </div>
            ))}
          </div>

      {draft ? (
        <ConstraintPanel
          key={JSON.stringify(draft.tables.map(t => t.seats.map(p => p.enrollmentId)))}
          sessionId={sessionId ?? ''}
          tables={draft.tables.map((table) => ({
            tableNumber: table.tableNumber,
            seats: table.seats.map((seat) => ({
              seat: seat.seat,
              enrollmentId: seat.enrollmentId,
            })),
          }))}
          nameOf={(id) => {
            const found = draft.tables
              .flatMap((table) => table.seats)
              .find((seat) => seat.enrollmentId === id);
            return found ? `${found.firstName} ${found.lastName}` : unseatedRoster.find(p => p.enrollmentId === id)?.name ?? 'that player';
          }}
          canApplySwap={(a, b) => !readOnly && problems.length === 0 && canApplySuggestion(a, b)}
          onApplySwap={applySwap}
        />
      ) : null}

          {published && !dirty && !editingFinalized && saved.data?.emailPreviewHref ? <p className="text-sm text-ink-2"><Link to={saved.data.emailPreviewHref} className={LINK_CLASS}>Preview seating email</Link></p> : null}

          <div className="flex flex-wrap items-center gap-3 border-t border-light pt-4">
            <div className="w-full" aria-live="polite">
              {!published && draft.tables.some(t => t.seats.length > 0 && t.seats.length < 4) ? <p className="text-sm text-ink-2">Some tables have fewer than 4 players.</p> : null}
              {error ? <ErrorNote>{error}</ErrorNote> : null}
              {!readOnly && visibleProblems.length ? <ul className="text-sm text-ink-2">{visibleProblems.map((problem,i) => <li key={i}>{problem}</li>)}</ul> : null}
              {affectedScored.length ? <p role="alert" className="text-sm text-rust">Keep recorded-score tables unchanged: {affectedScored.map(t => `Table ${t.tableNumber}`).join(', ')}. Use Undo before saving.</p> : null}
            </div>
            {editingFinalized ? <>
              <Button disabled={!dirty || busy || problems.length > 0 || affectedScored.length > 0} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Save changes'}</Button>
              <Button variant="secondary" disabled={busy} onClick={() => {
                setEditingFinalized(false); setDirty(false); setDraft(saved.data?.seating ?? null); setUndo([]); setPicked(null); save.reset(); sessionStorage.removeItem(recoveryKey); void saved.refetch();
              }}>Cancel</Button>
            </> : published && !closed ? <>
              <Button variant="secondary" disabled={busy} onClick={() => { setEditingFinalized(true); setConfirmReopen(false); setUndo([]); save.reset(); }}>Edit tables</Button>
              {!scored ? (confirmReopen ? <>
                <p className="w-full text-sm text-ink-2">Players will stop seeing their tables until you finalize again.</p>
                <Button variant="secondary" disabled={busy} onClick={() => reopen.mutate()}>{reopen.isPending ? 'Reopening…' : 'Reopen seating'}</Button>
                <Button variant="secondary" disabled={busy} onClick={() => { setConfirmReopen(false); reopen.reset(); }}>Cancel</Button>
              </> : <Button variant="secondary" disabled={busy} onClick={() => setConfirmReopen(true)}>Reopen seating</Button>) : null}
              {finalizedMessage ? <span role="status" className="text-[13px] text-ink-2">Seating finalized.</span> : null}
            </> : scored || closed ? (
              <span className="text-[13px] text-ink-3">These saved assignments cannot be edited here.</span>
            ) : (
              <>
                {/* One primary: Save while there is something to save, then Finalize. */}
                <Button variant={dirty ? 'primary' : 'secondary'} disabled={!dirty || busy} onClick={() => save.mutate()}>
                  {save.isPending ? 'Saving…' : dirty ? 'Save arrangement' : 'Arrangement saved'}
                </Button>
                <Button variant={dirty ? 'secondary' : 'primary'} disabled={busy || problems.length > 0} onClick={() => publish.mutate()}>
                  {publish.isPending ? 'Finalizing…' : 'Finalize seating'}
                </Button>
              </>
            )}
          </div>
        </>
      ) : null}

      {/*
        What the cursor is carrying. `pointer-events-none` is not decoration — without it the ghost
        sits under the pointer and `elementFromPoint` finds the ghost instead of the chair beneath
        it, so nothing is ever a drop target.

        It NAMES THE PERSON IT IS ABOUT TO DISPLACE, because it is sitting on top of her: the ghost
        follows the cursor, the cursor is over the chair, and the chair's own label is underneath.
        Without this the one fact a director needs before letting go — who she is swapping with — is
        the fact the ghost is covering.
      */}
      {drag.dragging ? (
        <div
          aria-hidden="true"
          className="pointer-events-none fixed z-50 flex items-center gap-1.5 rounded-md border border-deep bg-deep px-2.5 py-1.5 text-[13px] font-medium text-on-deep shadow-lg"
          style={{ left: drag.dragging.x + 14, top: drag.dragging.y + 14 }}
        >
          {drag.dragging.label}
          {swapPartner ? (
            <>
              <span aria-hidden="true" className="text-on-deep/60">
                &#8646;
              </span>
              {swapPartner}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * One table: a square with chairs around it, laid out counter-clockwise from East as they are in
 * the room. Seat identity remains internal.
 */
function Table({
  table,
  picked,
  disabled,
  drag,
  onPick,
  onPlace,
  onClear,
}: {
  onClear: () => void;
  table: SeatedTable;
  picked: string | null;
  disabled: boolean;
  drag: SeatDrag;
  onPick: (id: string | null) => void;
  onPlace: (tableNumber: number, seat: Seat) => void;
}) {
  const seatAt = (seat: Seat) => table.seats.find((s) => s.seat === seat) ?? null;
  const hasFloater = table.seats.some((s) => s.seat === 'floater') || table.seats.length === 5;


  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <h2 className="font-ui text-[13px] font-semibold uppercase tracking-[0.1em] text-ink-2">
          Table {table.tableNumber}
        </h2>
      </div>
      {!disabled ? <Button variant="secondary" size="inline" aria-label={`Clear table ${table.tableNumber}`} disabled={!table.seats.length} onClick={onClear}>Clear table</Button> : null}

      {/*
        East bottom, South right, West top, North left.

        The order East → South → West → North runs COUNTER-CLOCKWISE at a real table, and with East
        nearest the reader that puts South on the right. Laid out any other way the winds read
        backwards to anyone who actually plays, which is everyone using this screen.
      */}
      <div className="grid grid-cols-3 grid-rows-3 items-center justify-items-center gap-2 rounded-lg border border-light bg-felt/10 p-3">
        <div className="col-start-2 row-start-1 w-full">
          <Chair
            seat="west"
            tableNumber={table.tableNumber}
            player={seatAt('west')}
            picked={picked}
            disabled={disabled}
            drag={disabled ? null : drag}
            onPick={onPick}
            onPlace={() => onPlace(table.tableNumber, 'west')}
          />
        </div>
        <div className="col-start-1 row-start-2 w-full">
          <Chair
            seat="north"
            tableNumber={table.tableNumber}
            player={seatAt('north')}
            picked={picked}
            disabled={disabled}
            drag={disabled ? null : drag}
            onPick={onPick}
            onPlace={() => onPlace(table.tableNumber, 'north')}
          />
        </div>

        {/*
          THE FELT, and it carries the one thing that is otherwise four cards' worth of reading:
          the table number and current player count.

          It is also the only deep surface on the board, which is what makes a table read as a
          table rather than as a gap between four cards.
        */}
        <div className="col-start-2 row-start-2 flex size-full min-h-20 flex-col items-center justify-center rounded-md bg-deep px-2 py-2 text-center text-on-deep">
          {/* `-muted`: subtle is 3.64:1 on the felt, and 9px earns no relaxed bar at all. */}
          <span className="text-[11px] uppercase tracking-[0.10em] text-on-deep-2">
            Table
          </span>
          <span className="tabular font-display text-[26px] leading-none">{table.tableNumber}</span>
<span className="mt-1 text-[12px] text-on-deep-2">{table.seats.length} players</span>
        </div>

        <div className="col-start-3 row-start-2 w-full">
          <Chair
            seat="south"
            tableNumber={table.tableNumber}
            player={seatAt('south')}
            picked={picked}
            disabled={disabled}
            drag={disabled ? null : drag}
            onPick={onPick}
            onPlace={() => onPlace(table.tableNumber, 'south')}
          />
        </div>
        <div className="col-start-2 row-start-3 w-full">
          <Chair
            seat="east"
            tableNumber={table.tableNumber}
            player={seatAt('east')}
            picked={picked}
            disabled={disabled}
            drag={disabled ? null : drag}
            onPick={onPick}
            onPlace={() => onPlace(table.tableNumber, 'east')}
          />
        </div>

        {hasFloater ? (
          <div className="col-start-3 row-start-3 w-full">
            <Chair
              seat="floater"
              tableNumber={table.tableNumber}
              player={seatAt('floater')}
              picked={picked}
              disabled={disabled}
              drag={disabled ? null : drag}
              onPick={onPick}
              onPlace={() => onPlace(table.tableNumber, 'floater')}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Internal wind identities stay unchanged; accessible controls use visible positions. */
const SEAT_POSITION: Record<Seat, string> = { east: 'bottom', south: 'right', west: 'top', north: 'left', floater: 'extra' };


function Chair({
  seat,
  tableNumber,
  player,
  picked,
  disabled,
  drag,
  onPick,
  onPlace,
}: {
  seat: Seat;
  tableNumber: number;
  player: SeatedPlayer | null;
  picked: string | null;
  disabled: boolean;
  /** Null once the arrangement is published — nothing on the board moves after that. */
  drag: SeatDrag | null;
  onPick: (id: string | null) => void;
  onPlace: () => void;
}) {
  const isPicked = player !== null && picked === player.enrollmentId;

  const key = chairKey(tableNumber, seat);
  const dragging = drag?.dragging ?? null;

  /** This chair is under the pointer and something is being carried. */
  const isTarget = dragging !== null && dragging.over === key;
  /** The chair the carried player left. Dimmed, so it reads as vacated rather than duplicated. */
  const isSource = dragging !== null && player?.enrollmentId === dragging.enrollmentId;
  /** Dropping here swaps, rather than moves — worth saying before she lets go, not after. */
  const wouldSwap = isTarget && player !== null && !isSource;

  return (
    <button
      type="button"
      disabled={disabled}
      /*
       * How the drag finds its target. Read back out of the document with `elementFromPoint`
       * rather than tracked as rects, which go stale every time the board re-sorts its chairs.
       */
      data-chair={key}
      /*
       * The keyboard path the spec asks for is already here — every chair is a real button, so tab
       * and Enter do exactly what tapping does. What was missing is anybody being TOLD: without
       * these, a screen-reader user hears "Judith Lindqvist, advanced" whether she is holding that
       * player or about to place somebody else in her chair, which are opposite things.
       */
      aria-pressed={isPicked}
      aria-label={
        player
          ? picked && !isPicked
            ? `Put them in ${SEAT_POSITION[seat]} seat at Table ${tableNumber}, where ${player.firstName} ${player.lastName} is sitting — they will swap`
            : isPicked
              ? `${player.firstName} ${player.lastName} is picked up. Choose a chair.`
              : `Pick up ${player.firstName} ${player.lastName}, ${SEAT_POSITION[seat]} seat at Table ${tableNumber}`
          : picked
            ? `Put them in ${SEAT_POSITION[seat]} seat at Table ${tableNumber}, which is empty`
            : `${SEAT_POSITION[seat]} seat at Table ${tableNumber}, empty`
      }
      onPointerDown={(event) => {
        if (!player || !drag) return;
        drag.start(player.enrollmentId, `${player.firstName} ${player.lastName}`, event);
      }}
      onClick={() => {
        if (disabled) return;
        /** A drag ends in a click the browser invents. Ignoring it stops the drop being undone. */
        if (drag?.swallowClick()) return;
        // With someone in hand, every chair is a destination. Otherwise a chair picks its player up.
        if (picked) onPlace();
        else if (player) onPick(player.enrollmentId);
      }}
      className={[
        'flex min-h-[4.5rem] w-full flex-col items-center justify-center gap-0.5 rounded-md border px-2.5 py-2 text-center transition-colors',
        /*
          EXACTLY ONE BORDER-COLOUR CLASS. Two of them — `border-stroke` and `border-signal`
          — are the same specificity, so which one wins depends on the order Tailwind happens to
          emit them in rather than on the order they are written here. The accent on East silently
          lost that fight the first time this was built.
        */
        isPicked
          ? 'border-deep bg-deep text-on-deep'
          : !player
            ? 'border-dashed border-light bg-paper/40 hover:bg-sand'
            : 'border-control-border bg-paper hover:bg-sand',
        /*
         * Two different promises, so they cannot look the same: a solid ring means "she lands
         * here", and the amber one means "these two change places". A director dropping onto a
         * full table needs to know she is about to move somebody else as well.
         */
        isTarget ? (wouldSwap ? 'ring-2 ring-signal ring-offset-1' : 'ring-2 ring-deep ring-offset-1') : '',
        isSource ? 'opacity-40' : '',
        disabled ? 'cursor-default' : player ? 'cursor-grab' : '',
        /*
          THE DEALER AND FIFTH CHAIRS NO LONGER CARRY A RED BORDER.

          They did, and it cost the board its accent. Five tables meant five red cards before
          anything had happened — and dealer and fifth got the SAME red, so it did not even separate
          the two roles it was marking. The one genuinely urgent control on the screen, the red
          "Apply the swap" button, was competing with seven red borders that meant nothing urgent.

          The roles are still marked, in the place that says what they are: the word "Dealer" and
          "Fifth" beside the wind character. A word survives being scanned past; a border colour
          spent on every fifth card does not.
        */
      ].join(' ')}
    >
      {/*
        Wind and preferred pace wrap independently so full, readable labels fit narrow seats.
        The name remains the primary text; experience is not presented as playing pace.
      */}
      {/*
        THE NAME IS CENTRED IN A FIXED BOX, and that box is the reason every card is the same
        height. Names wrap to two lines and some do not; "Kimberly Ellsworth" beside "Julie Draper"
        with the box removed gives one table four different-sized chairs, which reads as though the
        board is describing something about them.
      */}
      <span className="flex min-h-[2.4rem] w-full items-center justify-center text-[13px] font-semibold leading-tight">
        {player ? `${player.firstName} ${player.lastName}${player.isSubstitute ? " · Substitute" : ""}` : 'Empty seat'}
      </span>
      {player ? <span className="text-[11px] font-normal leading-snug text-ink-2">{paceLabel(player.pace)}</span> : null}
    </button>
  );
}

/**
 * A recovered seating draft is used only if every table has a number and a list of seats, each seat
 * naming its enrollment. Anything else is ignored and the saved seating shown: a draft cut short used to
 * be able to blank the board, and stayed in the tab to do it again on every reload (2026-10-09).
 */
function recoveryFits(recovered: { tables?: unknown }): boolean {
  return Array.isArray(recovered.tables) && recovered.tables.every((table) => {
    const t = table as { tableNumber?: unknown; seats?: unknown } | null;
    return !!t && Number.isInteger(t.tableNumber) && Array.isArray(t.seats)
      && t.seats.every((seat) => !!seat && typeof (seat as { enrollmentId?: unknown }).enrollmentId === 'string');
  });
}
